import React, {useEffect, useRef, useState} from 'react';
import {List, Button} from 'antd';
import {
    CalendarOutlined,
    IdcardOutlined,
    SettingOutlined,
    DashboardFilled,
    FileExcelFilled,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {historyExportToExcel} from '../../../components/historyExcelExport';
import {useSelector} from 'react-redux';

const HistorySideBar = ({data, currentIndex, onItemSelect}) => {
    const listRef = useRef(null);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [addressData, setAddressData] = useState({});
    const [gearData, setGearData] = useState({});
    const [speedData, setSpeedData] = useState({});
    const [loadingAddress, setLoadingAddress] = useState(false);
    const [userState, setUserState] = useState(null);
    const [filledData, setFilledData] = useState([]);

    const {user} = useSelector((state) => state.auth);

    const getFullKey = (lat, lon) => {
        const latStr = parseFloat(lat).toFixed(6).split('.')[1].slice(0, 3);
        const lonStr = parseFloat(lon).toFixed(6).split('.')[1].slice(0, 3);
        return `${latStr},${lonStr}`;
    };

    const getCachedAddress = (key) => {
        const cachedData = localStorage.getItem('addressCache');
        if (cachedData) {
            const cache = JSON.parse(cachedData);
            return cache[key] || null;
        }
        return null;
    };

    const setCachedAddress = (key, address) => {
        const cachedData = localStorage.getItem('addressCache');
        const cache = cachedData ? JSON.parse(cachedData) : {};
        cache[key] = address;
        localStorage.setItem('addressCache', JSON.stringify(cache));
    };

    const fetchAddress = async (lat, lon) => {
        const key = `${parseFloat(lat).toFixed(6)},${parseFloat(lon).toFixed(6)}`;
        const cachedAddress = getCachedAddress(key);
        if (cachedAddress) {
            return cachedAddress;
        }

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
            );
            const result = await response.json();
            const address = result.display_name || 'Adres bulunamadı';
            setCachedAddress(key, address);
            return address;
        } catch (error) {
            console.error(error);
            return 'Adres alınamadı';
        }
    };

    // Paralel isteklerle adres sorgulama
    const fetchAddressesInBatches = async (data, batchSize = 5) => {
        setLoadingAddress(true);
        const batches = [];
        for (let i = 0; i < data.length; i += batchSize) {
            batches.push(data.slice(i, i + batchSize));
        }

        for (const batch of batches) {
            const promises = batch.map(async (item) => {
                if (item.gps && item.gps.lat && item.gps.lon) {
                    const key = getFullKey(item.gps.lat, item.gps.lon);
                    const fullKey = `${parseFloat(item.gps.lat).toFixed(6)},${parseFloat(item.gps.lon).toFixed(6)}`;

                    if (!addressData[fullKey]) {
                        const address = await fetchAddress(item.gps.lat, item.gps.lon);
                        return {key, fullKey, address};
                    }
                }
                return null;
            });

            const results = await Promise.all(promises);
            results.forEach((result) => {
                if (result) {
                    setAddressData((prev) => ({
                        ...prev,
                        [result.key]: result.address,
                        [result.fullKey]: result.address,
                    }));
                }
            });

            // Her batch arasında 2 saniye bekle (API sınırlamalarına uymak için)
            await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        setLoadingAddress(false);
    };

    useEffect(() => {
        if (user && user.name) {
            setUserState(user.name);
        }
    }, [user]);

    useEffect(() => {
        if (listRef.current) {
            const currentItem = listRef.current.querySelector(`#item-${currentIndex}`);
            if (currentItem) {
                currentItem.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
        }
    }, [currentIndex]);

    // filledData güncellemesi
    useEffect(() => {
        let lastValidCondition = null;
        let lastValidGear = null;
        let lastValidSpeed = null;
        const updatedData = data.map((item) => {
            if (item.conditions && item.conditions.length > 0) {
                lastValidCondition = item.conditions;
            }
            if (item.currentgear) {
                lastValidGear = item.currentgear;
            }
            if (!isNaN(item.wheelbasedspeed)) {
                lastValidSpeed = item.wheelbasedspeed;
            }
            return {
                ...item,
                conditions: (item.conditions && item.conditions.length > 0)
                    ? item.conditions
                    : (lastValidCondition || []),
                currentgear: item.currentgear || lastValidGear || '-',
                wheelbasedspeed: !isNaN(item.wheelbasedspeed)
                    ? item.wheelbasedspeed
                    : (lastValidSpeed || 0),
            };
        });
        setFilledData(updatedData);
    }, [data]);

    // Adres sorgularını başlat
    useEffect(() => {
        if (filledData.length > 0) {
            fetchAddressesInBatches(filledData);
        }
    }, [filledData]);

    // Vites ve hız bilgilerini güncelle
    useEffect(() => {
        const newGearData = {};
        const newSpeedData = {};
        filledData.forEach((item, index) => {
            newGearData[index] = item.currentgear || '-';
            newSpeedData[index] =
                !isNaN(item.wheelbasedspeed)
                    ? `${item.wheelbasedspeed} km/h`
                    : ' km/h';
        });
        setGearData(newGearData);
        setSpeedData(newSpeedData);
    }, [filledData]);

    const toggleExpand = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    const getBackgroundColor = (item, index) => {
        if (index === currentIndex) {
            return 'rgba(173, 216, 230, 0.5)';
        }
        if (item.conditions.some((cond) => cond.status === 'red')) {
            return 'rgba(255, 0, 0, 0.2)';
        }
        if (item.conditions.some((cond) => cond.status === 'yellow')) {
            return 'rgba(255, 255, 0, 0.2)';
        }
        return 'transparent';
    };

    return (
        <div
            style={{
                position: 'absolute',
                top: 50,
                left: 10,
                height: '80%',
                width: '30%',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                overflow: 'hidden',
                padding: '10px',
                zIndex: 1000,
                borderRadius: 5,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    marginBottom: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '10px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    width: '100%',
                }}
            >
                <Button
                    block
                    type="primary"
                    size="normal"
                    icon={<FileExcelFilled/>}
                    onClick={() => historyExportToExcel(data, addressData, userState)}
                >
                    Raporla
                </Button>
            </div>
            <div ref={listRef} style={{overflowY: 'auto', height: 'calc(100% - 80px)'}}>
                <List
                    itemLayout="horizontal"
                    dataSource={filledData}
                    renderItem={(item, index) => (
                        <List.Item
                            id={`item-${index}`}
                            style={{
                                backgroundColor: getBackgroundColor(item, index),
                                padding: '10px',
                                marginBottom: '5px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                            }}
                            onClick={() => onItemSelect(index)}
                        >
                            <List.Item.Meta
                                title={
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <IdcardOutlined style={{marginRight: '8px'}}/>
                                        <span style={{marginRight: '8px'}}>{item.plate_number}</span>
                                        <span>{item.chassisNumber}</span>
                                    </div>
                                }
                                description={
                                    <>
                                        <div style={{marginBottom: '8px'}}>
                                            <strong>Adres:</strong>{' '}
                                            {item.gps && item.gps.lat && item.gps.lon
                                                ? (addressData[`${parseFloat(item.gps.lat).toFixed(6)},${parseFloat(item.gps.lon).toFixed(6)}`] ||
                                                    (loadingAddress ? 'Alınıyor...' : 'Adres alınamadı'))
                                                : 'Adres yok'}
                                        </div>
                                        <div style={{display: 'flex', alignItems: 'center'}}>
                                            <CalendarOutlined style={{marginRight: '8px'}}/>
                                            <span>
                                                {item.createdat
                                                    ? moment(item.createdat, 'YYYY-MM-DD HH:mm:ss.SSSSSS').format('DD/MM/YYYY HH:mm:ss')
                                                    : '-'}
                                            </span>
                                            <SettingOutlined style={{marginLeft: '16px', marginRight: '8px'}}/>
                                            <span>{gearData[index] || '-'}</span>
                                            <DashboardFilled style={{marginLeft: '16px', marginRight: '8px'}}/>
                                            <span>{speedData[index] || ' km/h'}</span>
                                        </div>
                                        {item.conditions.length > 0 && (
                                            <>
                                                <Button
                                                    type="link"
                                                    icon={expandedIndex === index ? <UpOutlined/> : <DownOutlined/>}
                                                    onClick={() => toggleExpand(index)}
                                                    style={{marginTop: '10px'}}
                                                >
                                                    {expandedIndex === index
                                                        ? 'Uyarı & Hataları gizle'
                                                        : 'Uyarı & Hataları göster'}
                                                </Button>
                                                {expandedIndex === index && (
                                                    <div style={{marginTop: '10px'}}>
                                                        <List
                                                            size="small"
                                                            dataSource={item.conditions}
                                                            renderItem={(condition) => (
                                                                <List.Item>
                                                                    <List.Item.Meta title={condition.name}/>
                                                                </List.Item>
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                }
                            />
                        </List.Item>
                    )}
                />
            </div>
        </div>
    );
};

export default HistorySideBar;